<template>
  <div>
    <a-card :loading="spinning">
      <a-page-header :sub-title="`需求详情 # PR-${storyId}`">
        <template slot="title">
          <div v-if="storyData.is_temporary" class="corner" />
          <span>{{ storyData.title }}</span>
        </template>
        <template slot="extra">
          <a-button v-tc="{ name: '需求提审' }" type="primary" @click="onToQA">提审</a-button>
          <a-button v-tc="{ name: '编辑需求' }" type="primary" @click="onEdit">编辑</a-button>
          <a-dropdown>
            <a-menu slot="overlay">
              <a-menu-item key="1">
                <a v-tc="{ name: '删除需求' }" @click="onDelete"><a-icon type="delete" /> 删除需求</a>
              </a-menu-item>
              <a-menu-item v-if="storyData.is_temporary" key="2" v-has="{ code: 'project:requirement:temporary:update', project: projectId }">
                <a v-tc="{ name: '标记临时需求' }" @click="onSetTemporary(0)"><a-icon type="pushpin" /> 取消临时需求</a>
              </a-menu-item>
              <a-menu-item v-else key="3" v-has="{ code: 'project:requirement:temporary:update', project: projectId }">
                <a v-tc="{ name: '标记临时需求' }" @click="onSetTemporary(1)"><a-icon type="pushpin" /> 标为临时需求</a>
              </a-menu-item>
              <a-menu-item key="4">
                <a v-tc="{ name: '记录变更' }" @click="recordChange"><a-icon type="highlight" /> 记录变更</a>
              </a-menu-item>
              <a-menu-item key="5">
                <a v-tc="{ name: '移动项目' }" @click="onMoveProject"><a-icon type="export" /> 移动项目</a>
              </a-menu-item>
            </a-menu>
            <a-button> 更多 <a-icon type="down" /> </a-button>
          </a-dropdown>
        </template>
        <a-descriptions size="small" :column="3">
          <a-descriptions-item label="状态"> <a-badge v-if="storyData.status" :status="storyStatus[storyData.status].status" :text="storyStatus[storyData.status].label" /> </a-descriptions-item>
          <a-descriptions-item label="标签">
            <template slot="content">
              <a-tag v-for="(tag, index) in filteredTags(storyData.tags)" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
            </template>
            <template slot="title">
              <span>标签</span>
            </template>
            <a-tag v-for="(tag, index) in filteredTags(storyData.tags)" :key="index" :color="tag.color">{{ tag.name }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="是否拆分完成">
            <span v-if="storyData.split_done == 1">已拆分完成</span>
            <span v-if="storyData.split_done == 0">未拆分完成</span>
          </a-descriptions-item>
          <a-descriptions-item label="类型"> <a-badge v-if="storyData.status" :color="storyTypes[storyData.requirement_type].color" :text="storyTypes[storyData.requirement_type].label" /> </a-descriptions-item>
          <a-descriptions-item label="优先级">{{ storyData.priority || '--' }}</a-descriptions-item>
          <a-descriptions-item label="负责人">
            <span v-if="storyData.handlers && storyData.handlers.length > 0">
              <span v-for="user in users(storyData.handlers)" :key="user.id">
                <a-avatar :size="20" :src="user.avatar_url" /> <span class="nickname">{{ user.nickname }} </span>
              </span>
            </span>
            <span v-else>--</span>
          </a-descriptions-item>
          <a-descriptions-item label="评审时间">
            {{ storyData.is_review || '--' }}
          </a-descriptions-item>
          <a-descriptions-item label="当前进度">
            <a-tooltip v-if="storyData.progress" :title="`已完成: ${storyData.progress.completed} / 进行中: ${storyData.progress.inProgress} / 未开始: ${storyData.progress.new}`">
              <a-progress :percent="storyData.progress.progress" size="small" style="width: 180px" />
            </a-tooltip>
          </a-descriptions-item>
          <a-descriptions-item v-if="storyData.version" label="所属迭代"> {{ storyData.version.name || null }} </a-descriptions-item>
          <a-descriptions-item label="开始时间"> {{ storyData.start_time || '--' }} </a-descriptions-item>
          <a-descriptions-item label="结束时间"> {{ storyData.end_time || '--' }} </a-descriptions-item>
          <a-descriptions-item v-if="storyData.creator" label="创建人">
            <user-profiles :value="[storyData.creator.id]" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="创建时间"> {{ storyData.created_time }} </a-descriptions-item>
          <a-descriptions-item label="更新时间"> {{ storyData.updated_time }} </a-descriptions-item>
          <a-descriptions-item label="预计上线时间"> {{ storyData.pre_online_time || '--' }} </a-descriptions-item>
          <a-descriptions-item label="技术评审时间"> {{ storyData.technical_review || '--' }} </a-descriptions-item>
          <a-descriptions-item label="技术负责人">
            <user-profiles v-if="storyData.tech_owner" :value="storyData.tech_owner.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="产品">
            <user-profiles v-if="storyData.pm" :value="storyData.pm.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="运营">
            <user-profiles v-if="storyData.operators" :value="storyData.operators.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="质量">
            <user-profiles v-if="storyData.qa" :value="storyData.qa.map(item => item.id)" avatar />
          </a-descriptions-item>
          <a-descriptions-item label="ui设计">
            <user-profiles v-if="storyData.designer" :value="storyData.designer.map(item => item.id)" avatar />
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>
      <a-tabs v-model="tabsCurrent" default-active-key="1" size="small" @change="handleTabsChange">
        <a-tab-pane key="1" tab="详细信息">
          <template v-if="storyData.description">
            <am-editor :content="storyData.description" model="view" :drag="false" />
          </template>
          <template v-else>
            <story-document document-type="PRD" :data-source="documents" :source-id="storyId" :title="storyData.title" @ok="getDocuments" />
          </template>
        </a-tab-pane>
        <a-tab-pane v-if="storyData.parent_req && storyData.parent_req.length > 0" key="12" tab="父需求">
          <sub-story-list ref="parentRequirements" :data="storyData.parent_req" />
        </a-tab-pane>
        <a-tab-pane key="2">
          <span slot="tab">子需求 <a-badge :count="subRequirements.length" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <a-button v-if="storyData.status != 7" v-tc="{ name: '创建子需求' }" icon="plus" type="link" style="margin-bottom: 8px" @click="onAddSub">创建子需求</a-button>
          <sub-story-list ref="subRequirements" :data="subRequirements" />
        </a-tab-pane>
        <a-tab-pane v-if="subRequirements.length === 0" key="3" force-render>
          <span slot="tab">关联任务 <a-badge :count="storyInfo.taskNum" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <a-button v-if="storyData.status != 7" v-tc="{ name: '创建任务' }" icon="plus" type="link" style="margin-bottom: 8px" @click="onAddTask">创建任务</a-button>
          <task-list :ref="storyData.id" :story-id="storyData.id" @update="updateItem" />
        </a-tab-pane>
        <a-tab-pane key="9">
          <span slot="tab">任务分布</span>
          <task-heat :story-id="storyId" />
        </a-tab-pane>
        <a-tab-pane v-if="false" key="11">
          <span slot="tab">理想任务分布</span>
          <task-heat :story-id="storyId" />
        </a-tab-pane>
        <a-tab-pane key="4">
          <span slot="tab">关联缺陷 <a-badge :count="issueList.length" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <story-issue-list :issue-list="issueList" :loading="loading" />
        </a-tab-pane>
        <a-tab-pane v-if="storyData.workflows && storyData.workflows.length > 0" key="5">
          <span slot="tab">关联流程 <a-badge :count="storyData.workflows.length" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <workflow-data-list :table-data="storyData.workflows" :pagination="false" category="story" />
        </a-tab-pane>
        <a-tab-pane v-if="storyData.releases && storyData.releases.length > 0" key="6">
          <span slot="tab">发布评审 <a-badge :count="storyData.releases.length" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <workflow-release :table-data="storyData.releases" />
        </a-tab-pane>
        <a-tab-pane v-if="storyData.business_requirements && storyData.business_requirements.length > 0" key="10">
          <span slot="tab">业务需求 <a-badge :count="storyInfo.businessRequirementNum" :number-style="{ backgroundColor: '#438DF5' }" /></span>
          <business-requirement-list :data-source="storyData.business_requirements" @countChange="handleCountChange" />
        </a-tab-pane>
        <a-tab-pane v-if="storyData.cases && storyData.cases.length > 0" key="8" tab="关联用例">
          <case-list :table-data="storyData.cases" />
        </a-tab-pane>
        <a-tab-pane key="13" tab="相关契约" force-render>
          <ApiVersionView ref="apiVersionViewRef" type="story" />
        </a-tab-pane>
        <a-tab-pane key="14" tab="待办任务" force-render>
          <story-document document-type="TODO" :data-source="documents" :source-id="storyId" :title="storyData.title" @ok="getDocuments" />
        </a-tab-pane>
        <a-tab-pane key="15" tab="测试计划">
          <story-testplan v-if="tabsCurrent === '15'" :requirement-id="storyId" />
        </a-tab-pane>
        <a-tab-pane key="7" tab="变更历史">
          <story-changelog ref="changelog" :story-id="storyId" />
        </a-tab-pane>
      </a-tabs>
      <comment-panel ref="commentRef" mode="requirement" :source-id="storyId" />
    </a-card>
    <a-modal v-model="changeVisible" width="1000px" title="请记录" @ok="handleOk">
      <a-textarea v-model="changeInfo" placeholder="请简要概括变更信息" allow-clear />
      <a-table :data-source="storyData.change_info" :columns="columns">
        <template #title>变更历史</template>
      </a-table>
    </a-modal>
    <a-back-top />
    <task-edit ref="taskEditRef" @close="reload" @cancel="onCancel" />
    <story-project-edit ref="storyProjectEditRef" @ok="onMoveOk" />
    <story-edit :data-source="storyData" :visible="editVisible" :parent="parent" :story-id="sourceId" @close="close" @cancel="onCancel" />
  </div>
</template>
<script>
import { issueList } from '@/api/project/issue';
import { queryDocument } from '@/api/business/document';
import { storyStatus, storyTypes } from '@/utils/dictionary';
import { storyDetail, storyUpdateItem, storyDelete, modifyChangeInfo, getReqTaskHeat } from '@/api/project/story';
import { createRootReq } from '@/api/project/workflow';
import AmEditor from '@/components/AmEditor/index';
import CommentPanel from '@/components/comment/CommentPanel';
import TaskEdit from '@/pages/project/task/components/TaskEdit';
import CaseList from '@/pages/project/story/components/CaseList';
import TaskHeat from '@/pages/project/story/components/TaskHeat';
import TaskList from '@/pages/project/version/components/TaskList';
import StoryEdit from '@/pages/project/story/components/StoryEdit';
import SubStoryList from '@/pages/project/story/components/SubStoryList';
import StoryDocument from '@/pages/project/story/components/StoryDocument';
import StoryTestplan from '@/pages/project/story/components/StoryTestplan';
import ApiVersionView from '@/pages/project/task/components/ApiVersionView';
import StoryChangelog from '@/pages/project/story/components/StoryChangelog';
import StoryIssueList from '@/pages/project/story/components/StoryIssueList';
import WorkflowRelease from '@/pages/project/story/components/WorkflowRelease';
import StoryProjectEdit from '@/pages/project/story/components/StoryProjectEdit';
import WorkflowDataList from '@/pages/project/workflow/components/WorkflowDataList';
import BusinessRequirementList from '@/pages/project/story/components/BusinessRequirementList';
// import IdealTaskHeat from '@/pages/project/story/components/IdealTaskHeat';

export default {
  components: {
    TaskEdit,
    TaskList,
    CaseList,
    AmEditor,
    TaskHeat,
    StoryEdit,
    CommentPanel,
    SubStoryList,
    StoryTestplan,
    StoryDocument,
    StoryChangelog,
    StoryIssueList,
    ApiVersionView,
    WorkflowRelease,
    WorkflowDataList,
    StoryProjectEdit,
    BusinessRequirementList
  },
  data() {
    return {
      storyTypes,
      changeInfo: null,
      storyStatus,
      spinning: true,
      loading: false,
      editVisible: false,
      activeKey: ['1'],
      projectId: parseInt(this.$route.params.project_id) || null,
      storyId: parseInt(this.$route.query.storyId) || null,
      storyData: { parent_req: [] },
      storyInfo: { taskNum: 0 },
      issueList: [],
      documents: [],
      subRequirements: [],
      tabsCurrent: '1',
      parent: {},
      sourceId: null,
      changeVisible: false,
      columns: [
        {
          title: '变更信息',
          dataIndex: 'change_info',
          key: 'change_info'
        },
        {
          title: '上报人',
          dataIndex: 'name',
          key: 'name'
        },
        {
          title: '变更时间',
          dataIndex: 'time',
          key: 'time'
        }
      ],
      tabItems: [
        { key: '1', label: '详细信息' },
        { key: '2', label: '子需求' },
        { key: '3', label: '关联任务' },
        { key: '4', label: '关联缺陷' },
        { key: '5', label: '关联流程' },
        { key: '6', label: '发布评审' },
        { key: '7', label: '变更历史' },
        { key: '8', label: '关联用例' },
        { key: '9', label: '任务分布' },
        { key: '10', label: '业务需求' },
        { key: '11', label: '理想任务分布' },
        { key: '12', label: '父需求' },
        { key: '13', label: '相关契约' },
        { key: '14', label: '待办任务' },
        { key: '15', label: '测试计划' }
      ]
    };
  },
  computed: {
    counts() {
      return (record, status) => {
        const p = record.progress.status;
        return p !== null && p[status] !== undefined ? p[status] : 0;
      };
    },

    progress() {
      return p => {
        let num = 0;
        let completed = 0;
        if (p === null) {
          return 0;
        }
        for (const item in p) {
          num += p[item];
          if (item === '3') {
            completed += p[item];
          }
        }
        return completed === 0 ? completed : Math.round((completed / num) * 100);
      };
    },
    total() {
      return p => {
        let count = 0;
        if (p !== null) {
          for (const item in p) {
            count += p[item];
          }
        }
        return count;
      };
    },
    users() {
      return items => {
        return items || [];
      };
    }
  },

  created() {
    if (this.storyId !== null) {
      this.getStoryDetail();
      this.getIssueList();
    } else {
      this.$router.push({ name: 'page:project:story' });
    }
  },
  methods: {
    handleCountChange(count) {
      this.$set(this.storyInfo, 'businessRequirementNum', count);
    },

    /**
     * 获取需求详情
     */
    getStoryDetail() {
      storyDetail({ requirement_id: this.storyId, project_id: this.projectId })
        .then(res => {
          this.storyData = res.data.item;
          this.spinning = false;
          this.subRequirements = res.data.item.sub_requirements;
          this.$nextTick(() => {
            this.$refs['apiVersionViewRef'].init(this.storyData.agreements, this.taskId);
          });
          this.getDocuments();
        })
        .catch(() => {
          this.spinning = false;
        });
    },
    getStoryHeat() {
      getReqTaskHeat({ root_req_id: this.storyId, req_list: [this.storyId] })
        .then(res => {
          this.storyData = res.data.item;
          this.spinning = false;
          this.subRequirements = res.data.item.sub_requirements;
        })
        .catch(() => {
          this.spinning = false;
        });
    },
    filteredTags(tags) {
      if (!Array.isArray(tags)) {
        tags = [];
      }
      const uniqueTags = tags.filter((tag, index) => {
        return tags.findIndex(t => t.id === tag.id) === index;
      });

      const tagCounts = tags.reduce((counts, tag) => {
        counts[tag.id] = (counts[tag.id] || 0) + 1;
        return counts;
      }, {});

      return uniqueTags.map(tag => {
        const count = tagCounts[tag.id];
        const processedTag = { ...tag }; // 创建一个新对象来保存处理后的标签
        if (count > 1) {
          processedTag.name = processedTag.name + count + '次';
        }
        return processedTag;
      });
    },

    reload() {
      if (this.$refs[this.storyId]) {
        this.$refs[this.storyId].getTaskList();
      }
    },

    onAddComment() {
      if ('1'.includes(this.activeKey) === false) {
        this.activeKey.push('1');
      }
      this.$refs['comment'].onAddComment();
    },

    close(type) {
      this.onCancel();
      this.parent = {};
      this.sourceId = null;
      this.editVisible = false;
      if (type !== 'cancel') {
        this.getStoryDetail();
      }
      if (this.$refs['changelog']) {
        this.$refs.changelog.getStoryChangelog();
      }
    },

    onCancel() {
      // 更新评论组件Key，否则会导致无法监听到键盘事件
      this.$nextTick(() => {
        this.$refs.commentRef.setKey();
      });
    },
    /**
     * 获取缺陷列表
     */
    getIssueList() {
      this.loading = true;
      issueList({ requirement_id: this.storyId, project_id: this.projectId })
        .then(res => {
          this.issueList = res.data.items;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    /**
     * 点击移动项目
     */
    onMoveProject() {
      this.$nextTick(() => {
        this.$refs.storyProjectEditRef.onOpen({ id: this.storyId, value: this.projectId });
      });
    },

    /**
     * 项目移动成功回调事件，跳转至缺陷详情页
     * @param {*} project_id
     */
    onMoveOk(project_id) {
      this.$router
        .push({
          name: 'page:project:story:detail',
          params: { project_id: project_id },
          query: { storyId: this.storyId }
        })
        .then(() => {
          this.$router.go(0);
        });
    },

    getSubRequirements() {
      this.filterForm.parent_id = parseInt(this.$route.query.storyId);
      this.$refs.subRequirements.handleChange();
    },

    handleTabsChange(activeKey) {
      const item = this.tabItems.find(item => item.key === activeKey);
      this.utils.trackClick({ name: '切换卡片', label: item ? item.label : activeKey });
      if (this.tabsCurrent === '2') {
        this.$nextTick(() => {
          console.log(this.tabsCurrent);
        });
      }
    },

    onSetTemporary(value) {
      const self = this;
      this.$confirm({
        title: '确认操作',
        content: `确认${value === 1 ? '标为临时' : '取消临时'}需求吗`,
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk() {
          storyUpdateItem({
            project_id: self.projectId,
            requirement_id: self.storyId,
            key: 'is_temporary',
            value: value
          }).then(() => {
            self.$message.success('更新成功');
            self.spinning = true;
            self.getStoryDetail();
          });
        }
      });
    },

    onEdit() {
      this.sourceId = this.storyId;
      this.editVisible = true;
    },
    onToQA() {
      createRootReq({
        project_id: this.projectId,
        issue_id: this.storyId,
        issue_type: 'requirement'
      })
        .then(res => {
          // 检查 can_add 字段
          if (res.data.can_review === false) {
            // 如果不能添加，弹出 reason 提示
            alert(res.data.reason);
          } else {
            // 如果可以添加（或 can_add 为 true/undefined 等情况），提示成功
            alert('操作成功');
            this.getStoryDetail();
            // 或者使用 this.$message.success('操作成功'); 如果你偏好消息提示
          }
        })
        // 重要：不要忘记处理可能的错误
        .catch(error => {
          console.error('请求失败:', error);
          alert('操作失败，请稍后重试');
          // 或者 alert(error.message);
        });
    },

    /**
     * 创建子需求
     */
    onAddSub() {
      this.parent = { key: this.storyId, label: this.storyData.title };
      this.editVisible = true;
    },

    /**
     * 创建任务
     */
    onAddTask() {
      if (this.storyData.status === 7) {
        alert('需求已经是上线状态，不能添加任务');
      }
      const requirement = { key: this.storyData.id, label: this.storyData.title };
      this.$nextTick(() => {
        this.$refs['taskEditRef'].onOpen(null, {}, requirement);
      });
    },

    recordChange() {
      this.changeVisible = true;
    },

    handleOk() {
      this.utils.trackClick({ name: '提交记录变更' });
      modifyChangeInfo({ id: this.storyId, change_info: this.changeInfo })
        .then(() => {
          this.$message.success('记录成功');
        })
        .catch(() => {
          this.loading = false;
        });
      this.changeVisible = false;
    },

    /**
     * 删除需求
     */
    onDelete() {
      const self = this;
      this.$confirm({
        title: '确认',
        content: '确认要删除这个需求吗',
        okText: '确定',
        okType: 'danger',
        cancelText: '取消',
        onOk() {
          storyDelete({ requirement_id: self.storyId, project_id: self.projectId }).then(() => {
            self.$message.success('删除成功');
            self.$router.push({ name: 'page:project:story' });
          });
        }
      });
    },

    /**
     * 更新指定项数据
     */
    updateItem(key, value) {
      this.storyInfo[key] = value;
    },

    /**
     * 查询需求关联的文档
     */
    getDocuments() {
      queryDocument({
        pageNumber: 1,
        pageSize: 10,
        objectId: this.storyId,
        objectType: 'requirement'
      }).then(res => {
        this.documents = res.data.items.map((item, index) => ({ index, ...item }));
      });
    }
  }
};
</script>

<style lang="less" scoped>
/deep/.ant-card-body {
  position: relative;
  padding: 16px;

  .ant-page-header {
    padding: 0;
  }

  .ant-tabs-nav .ant-tabs-tab {
    margin: 0 8px 0 0;
  }
}

/deep/.ql-container.ql-snow {
  border: none;
  background: #fafafa;
  padding: 16px // font-size: 14px;
}

.item-title {
  color: #1890ff;
  font-weight: 500;
  // display: block;
}

.collapse-button {
  color: #86909c;
}

.story-panel {
  border-radius: 4px;
  border: 0;
  background: #fff;
  overflow: hidden;
}

.corner {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent) #ec374d;
}

.corner::before {
  position: absolute;
  top: -11px;
  left: 24px;
  font-size: 12px;
  height: 16px;
  content: '临时';
  border-radius: 2px 2px 2px 0px;
  padding: 2px 4px;
  font-size: 12px;
  line-height: 12px;
  color: #fff;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.4), transparent) #ec374d;
  background-blend-mode: soft-light;
}

.corner::after {
  position: absolute;
  content: '';
  left: 24px;
  clip-path: polygon(0 0, 5px 0, 5px 5px);
  width: 12px;
  height: 12px;
  top: 5px;
  /* 颜色变暗：filter滤镜、半透明遮罩 */
  /* 使用filter滤镜 使颜色变暗 */
  /*filter: brightness(.6);*/
  /* 给颜色上添加一个半透明遮罩实现颜色变暗 */
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3));
  background-color: inherit;
  /* 继承 */
}
</style>
